<template>
  <div class="method-component-container">
    <base-loading-state v-if="isInitialLoading" />
    <div v-else-if="currentComponent" class="method-content">
      <h3 class="mb-4 text__topic-title">{{ currentComponent.title }}</h3>

      <base-text-editor-display>
        <div
          v-if="currentComponent.content"
          v-safe-html="currentComponent.content"
          class="content mb-4 text__topic-content"
        ></div>
      </base-text-editor-display>

      <div v-if="currentComponent.additional_resource" class="additional-resources mb-4">
        <h5>Additional Resources</h5>
        <div
          v-safe-html="currentComponent.additional_resource"
          class="text__additional-resources"
        ></div>
      </div>

      <div v-if="currentComponent.attachments.length > 0" class="references mb-4">
        <BaseFilesList class="w-100" :attachments="currentComponent.attachments" />
      </div>

      <div class="text-center mt-4">
        <div class="d-flex flex-column align-items-center justify-content-center">
          <template>
            <div class="d-flex align-items-center mb-3">
              <template v-if="isComponentRead">
                <font-awesome-icon icon="check" class="mr-2 text-success" />

                <span class="text-success mr-3">Completed</span>
              </template>
              <button
                v-if="!(isLastComponent && isComponentRead)"
                class="btn btn-primary"
                :disabled="isMarkingAsRead"
                :class="isComponentRead ? `button__go_to_next_topic` : `button__mark_as_read`"
                @click="markAsRead"
              >
                <span
                  v-if="isMarkingAsRead"
                  class="d-flex align-items-center justify-content-center"
                >
                  <b-spinner small label="Loading..." variant="light" class="mr-2" />

                  Marking as read...
                </span>
                <span v-else>
                  {{ buttonText }}
                </span>
              </button>
            </div>
            <template v-if="isLastComponent && areAllComponentsRead">
              <div class="text-success text__evidence-message">
                Great! It looks like you're ready to submit your evidence.
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
    <div v-else class="no-content">No method component available</div>
  </div>
</template>

<script>
import MethodComponent from '@/models/MethodComponent';
import BaseTextEditorDisplay from '@/components/base-components/BaseTextEditorDisplay';
import BaseFilesList from '@/components/base-components/BaseFilesList';
import BaseLoadingState from '@/components/base-components/BaseLoadingState';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'PageMethodComponent',

  components: {
    BaseLoadingState,
    BaseTextEditorDisplay,
    BaseFilesList,
    FontAwesomeIcon
  },

  props: {
    isPreview: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isInitialLoading: true,
      isMarkingAsRead: false,
      componentDetail: null
    };
  },

  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    methodComponentId() {
      return this.$route.query.methodComponent ? Number(this.$route.query.methodComponent) : null;
    },

    currentComponent() {
      return (
        this.componentDetail ||
        MethodComponent.query()
          .where('learning_unit_id', this.learningUnitId)
          .where('id', this.methodComponentId)
          .first()
      );
    },

    isPreviewMode() {
      return this.isPreview;
    },

    isComponentRead() {
      return (
        this.componentDetail?.is_read ||
        this.$parent.readComponentIds?.includes(this.methodComponentId) ||
        false
      );
    },

    buttonText() {
      if (this.isMarkingAsRead) return 'Marking as read...';
      if (this.isComponentRead) return 'Go to next topic';
      return 'Mark as Read';
    },

    isLastComponent() {
      const components = MethodComponent.query()
        .where('learning_unit_id', this.learningUnitId)
        .get();
      if (!components.length) return false;
      const currentIndex = components.findIndex((c) => c.id === this.methodComponentId);
      return currentIndex === components.length - 1;
    },

    areAllComponentsRead() {
      const components = MethodComponent.query()
        .where('learning_unit_id', this.learningUnitId)
        .get();

      if (!components.length) return false;

      return components.every((component) => this.$parent.readComponentIds?.includes(component.id));
    }
  },

  watch: {
    methodComponentId: {
      handler: 'fetchData',
      immediate: true
    }
  },

  created() {
    // Removed redundant fetchData call since the watcher handles initial load
  },

  methods: {
    async fetchData() {
      if (!this.methodComponentId) {
        this.isInitialLoading = false;
        return;
      }

      try {
        this.isInitialLoading = true;
        // Fetch full details including attachments in a single call
        const {
          response: { data }
        } = await MethodComponent.api().fetchById(this.methodComponentId);
        this.componentDetail = data;
      } catch (error) {
        console.error('Error fetching method component:', error);
        this.$toast?.error('Failed to load method component');
      } finally {
        this.isInitialLoading = false;
      }
    },

    async markAsRead() {
      if (!this.currentComponent) return;

      if (this.isComponentRead) {
        this.goToNextTopic();
        return;
      }

      this.isMarkingAsRead = true;
      try {
        await MethodComponent.api().markAsRead(this.currentComponent.id);

        // Update the readComponentIds in the parent component
        this.$root.$emit('method-component-read');

        // Update local state
        if (this.componentDetail) {
          this.componentDetail = {
            ...this.componentDetail,
            is_read: true
          };
        }

        this.$toast?.success('Marked as read successfully');
      } catch (error) {
        console.error('Error marking as read:', error);
        if (!this.isPreviewMode) {
          this.$toast?.error('Failed to mark as read');
        }
      } finally {
        this.isMarkingAsRead = false;
      }
    },

    async goToNextTopic() {
      try {
        const components = await MethodComponent.query()
          .where('learning_unit_id', this.learningUnitId)
          .get();

        // Find the index of current component
        const currentIndex = components.findIndex((c) => c.id === this.methodComponentId);

        if (currentIndex < components.length - 1) {
          const nextComponent = components[currentIndex + 1];
          this.$router.push({
            query: {
              ...this.$route.query,
              methodComponent: nextComponent.id
            }
          });
        }
      } catch (error) {
        console.error('Error navigating to next topic:', error);
        this.$toast?.error('Failed to navigate to next topic');
      }
    }
  }
};
</script>

<style lang="scss">
.method-component-container {
  padding: 1.5rem;
  background-color: white;

  .method-content {
    margin: auto;
    max-width: 960px;
  }

  .no-content {
    text-align: center;
    color: #6c757d;
    padding: 2rem;
  }

  .content {
    line-height: 1.6;
  }

  .additional-resources {
    padding-top: 1.5rem;
    border-top: 1px solid #dee2e6;
  }

  .references {
    display: flex;
    justify-content: center;
  }

  h5 {
    color: #495057;
    margin-bottom: 0.5rem;
    font-weight: 700;
  }

  .btn {
    padding: 0.5rem 1.5rem;
    font-weight: 500;
  }

  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .text__topic-title {
    font-size: 27px;
    font-weight: 700;
  }

  .container__attachment {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 760px;
  }

  .list-group-item {
    background-color: #f4f5fd;
    padding: 20px 32px;
    border-radius: 8px;
  }

  .fa-spinner {
    font-size: 14px;
  }

  .text__evidence-message {
    font-weight: 700;
    font-size: 16px;
  }
}
</style>
